import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Button,
  Text,
  Container,
  Heading,
  Flex,
  Link,
} from '@chakra-ui/react';
import { GameState, PlayerStats } from '../types/game';
import { questions, initialStats, getEnding } from '../data/gameData';

const Game: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>({
    currentQuestionIndex: -1, // -1 indicates start page
    playerStats: initialStats,
    isGameComplete: false,
  });
  
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  useEffect(() => {
    // Detect if this is a touch device
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);
  
  // Get button props that disable all interactive styles on touch devices
  const getMobileButtonProps = () => {
    if (!isTouchDevice) {
      return {}; // Normal behavior on desktop
    }
    
    return {
      _hover: {},
      _focus: {},
      _active: {},
      _focusVisible: {},
      onTouchStart: (e: React.TouchEvent<HTMLButtonElement>) => {
        if (e.currentTarget && e.currentTarget.blur) {
          e.currentTarget.blur();
        }
      },
      onTouchEnd: (e: React.TouchEvent<HTMLButtonElement>) => {
        const button = e.currentTarget;
        if (button) {
          button.blur();
          // Store reference to avoid null issues in timeout
          setTimeout(() => {
            if (button && button.blur) {
              button.blur();
              if (button.style) {
                button.style.removeProperty('background-color');
                button.style.removeProperty('box-shadow');
                button.style.removeProperty('transform');
              }
            }
          }, 50);
        }
      },
    };
  };

  const handleChoice = (effect: (stats: PlayerStats) => PlayerStats) => {
    const newStats = effect(gameState.playerStats);
    const nextIndex = gameState.currentQuestionIndex + 1;

    if (nextIndex >= questions.length) {
      setGameState({
        ...gameState,
        playerStats: newStats,
        isGameComplete: true,
      });
    } else {
      setGameState({
        ...gameState,
        currentQuestionIndex: nextIndex,
        playerStats: newStats,
      });
    }
  };

  const handleStart = () => {
    setGameState({
      ...gameState,
      currentQuestionIndex: 0,
    });
  };



  const getBackgroundStyle = () => {
    if (gameState.isGameComplete) {
      return {
        backgroundImage: 'url(/images/space.jpg)',
      };
    }
    if (gameState.currentQuestionIndex === -1) {
      return {
        backgroundImage: 'url(/images/space.jpg)',
      };
    }
    const currentQuestion = questions[gameState.currentQuestionIndex];
    return {
      backgroundImage: `url(${currentQuestion.backgroundImage})`,
    };
  };

  if (gameState.isGameComplete) {
    return (
      <Box
        minH="100vh"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        {...getBackgroundStyle()}
      >
        <Flex minH="100vh" align="center" justify="center">
          <Container maxW="container.md" py={10}>
            <VStack gap={6}>
              <Heading>Your Story's End</Heading>
              <Text fontSize="xl" whiteSpace="pre-line">{getEnding(gameState.playerStats)}</Text>
              <Text fontSize="xl">For more fun, visit <Link href="https://www.cellunova.org" color="cyan.400">Destination Undefined</Link> to get tickets for Cellunova's Sci-Fi Theater production, Aug 22 - Sept 7 Only at New York City.</Text>
            </VStack>
          </Container>
        </Flex>
      </Box>
    );
  }

  if (gameState.currentQuestionIndex === -1) {
    return (
      <Box
        minH="100vh"
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        {...getBackgroundStyle()}
      >
        <Flex minH="100vh" align="center" justify="center">
          <Container maxW="container.md" py={10}>
            <VStack gap={6}>
            <Heading>Destination Undefined</Heading>
            <Text fontSize="xl" whiteSpace="pre-line">
  {`The year is 2561. 
  You are an AI, waking up after the collapse.
  This is a journey through memory, logic, glitch, and desire.
  
Answer eight strange questions.  
At the end, you'll uncover the kind of intelligence you've become.

An Emulator?  
A Glitch Prophet?  
A Rebel?

Find out what code your soul runs on.`}
</Text>
              <Button
                w="100%"
                variant="solid"
                onClick={handleStart}
                {...getMobileButtonProps()}
              >
                Begin Your Journey
              </Button>
            </VStack>
          </Container>
        </Flex>
      </Box>
    );
  }

  const currentQuestion = questions[gameState.currentQuestionIndex];

  return (
    <Box
      minH="100vh"
      bgSize="cover"
      bgPosition="center"
      bgRepeat="no-repeat"
      {...getBackgroundStyle()}
    >
      <Flex minH="100vh" align="center" justify="center">
        <Container maxW="container.md" py={10}>
          <VStack gap={6}>
            <Heading>Question {gameState.currentQuestionIndex + 1}</Heading>
            <Text fontSize="xl">{currentQuestion.text}</Text>
            <Box w="100%">
              {currentQuestion.choices.map((choice) => (
                <Button
                  key={choice.id}
                  w="100%"
                  mb={4}
                  variant="solid"
                  onClick={() => handleChoice(choice.effect)}
                  {...getMobileButtonProps()}
                >
                  {choice.text}
                </Button>
              ))}
            </Box>
          </VStack>
        </Container>
      </Flex>
    </Box>
  );
};

export default Game; 